import React, { useState, useEffect } from 'react';
import { voucherActions } from '../../../_actions/voucher.actions';
import { hairdresserService } from '../../../_services/hairdresser.service';
import { productService } from '../../../_services/product.service';
import { formatCurrency } from '../utils/checkoutCalculations';
import VoucherAmountFields from '../../gift-cards-v2/VoucherAmountFields';
import { introServiceCategories, introProducts, introVoucherCatalogs } from '../../onboarding/bailey-intro/introStage';

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

/**
 * AddItemScreen
 * Step 1: Choose type (Service / Product / Voucher)
 * Step 2: Pick from list
 */
// `practice`: Bailey's welcome. Example services, products and gift cards,
// nothing read from the salon's real catalogue.
const AddItemScreen = ({ state, actions, dispatch, onBack, currencySymbol, practice = false }) => {
  const [step, setStep] = useState('type'); // 'type' | 'services' | 'products' | 'vouchers'
  const [searchQuery, setSearchQuery] = useState('');
  const [serviceCategories, setServiceCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [voucherCatalogs, setVoucherCatalogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pickerVoucher, setPickerVoucher] = useState(null);

  const fmt = (amount) => formatCurrency(amount, currencySymbol);

  // Load services when entering service picker
  useEffect(() => {
    if (step !== 'services') return;
    if (practice) {
      setServiceCategories(introServiceCategories().map((c) => ({ ...c, services: c.hairdresserService })));
      return;
    }
    const hairdresserId = state.lineItems[0]?.staffId || localStorage.getItem('userId');
    if (!hairdresserId) return;
    setLoading(true);

    hairdresserService.getServicesGroupedV2({ hairdresserId })
      .then(response => {
        const categories = response?.data?.data?.hairdresserSelectedServices || [];
        setServiceCategories(Array.isArray(categories) ? categories : []);
        setLoading(false);
      })
      .catch(() => { setLoading(false); });
  }, [step]);

  // Load products when entering product picker
  useEffect(() => {
    if (step !== 'products') return;
    if (practice) { setProducts(introProducts()); return; }
    setLoading(true);

    const salonId = state.salonId || localStorage.getItem('salonId');
    productService.getProductsList({ salonId: salonId || '', search: '' })
      .then(response => {
        const data = response?.data?.data || response?.data?.responseData || {};
        // Products may be nested under categories or as a flat list
        let list = data.products || data.productList || data.result || [];
        // If response is category-based, flatten products from categories
        if (list.length === 0 && Array.isArray(data)) {
          list = data.reduce((acc, cat) => [...acc, ...(cat.products || [])], []);
        }
        // Also check if the response itself is an array of categories with nested products
        if (list.length === 0 && response?.data?.data && Array.isArray(response.data.data)) {
          list = response.data.data.reduce((acc, cat) => [...acc, ...(cat.products || [])], []);
        }
        setProducts(Array.isArray(list) ? list : []);
        setLoading(false);
      })
      .catch(() => { setLoading(false); });
  }, [step]);

  // Load voucher catalogs when entering voucher picker
  useEffect(() => {
    if (step !== 'vouchers') return;
    if (practice) { setVoucherCatalogs(introVoucherCatalogs()); return; }
    setLoading(true);

    dispatch(voucherActions.getVoucherCatalogs())
      .then(response => {
        const responseData = response?.data?.responseData || response?.data?.data || {};
        const data = responseData?.data || responseData || {};
        const catalogs = data.catalogs || data.result || [];
        // Only show active catalogs enabled for POS
        const activeCatalogs = (Array.isArray(catalogs) ? catalogs : []).filter(c =>
          c.status === 'active' && (c.channels?.pos === true || (Array.isArray(c.channels) && c.channels.includes('pos')))
        );
        setVoucherCatalogs(activeCatalogs);
        setLoading(false);
      })
      .catch(() => { setLoading(false); });
  }, [step]);

  // Tapping a voucher moves to the inline amount step (denomination / custom +
  // promo) rather than adding the base value straight away.
  const handleSelectVoucher = (catalog) => {
    setPickerVoucher(catalog);
    setStep('voucherAmount');
  };

  // The picker returns the chosen face value + the (promo) charge collected.
  const handleVoucherAmountConfirmed = ({ faceValue, charge }) => {
    const catalog = pickerVoucher;
    const staffId = state.booking?.hairdresserId || state.lineItems[0]?.staffId || localStorage.getItem('userId');
    const staffName = state.lineItems[0]?.staffName || state.booking?.hairdresser?.name || '';

    actions.addLineItem({
      type: 'voucher',
      id: `voucher_added_${Date.now()}`,
      serviceId: catalog._id,
      name: catalog.name || 'Gift Voucher',
      originalPrice: charge,   // collected at the till (promo price when discounted)
      faceValue,               // redeemable value issued on the card
      staffId,
      staffName,
      discount: null,
      diaryColour: null,
      voucherCatalogId: catalog._id,
    });
    setPickerVoucher(null);
    setStep('vouchers');
    onBack();
  };

  const handleSelectService = (service, categoryName) => {
    const staffId = state.booking?.hairdresserId || state.lineItems[0]?.staffId || localStorage.getItem('userId');
    const staffName = state.lineItems[0]?.staffName || state.booking?.hairdresser?.name || '';

    actions.addLineItem({
      type: 'service',
      id: `service_added_${Date.now()}`,
      serviceId: service.serviceId || service._id,
      name: service.serviceName || '',
      originalPrice: parseFloat(service.servicePrice) || 0,
      staffId,
      staffName,
      discount: null,
      diaryColour: service.diaryColour || '#D6D5EC',
      serviceTime: service.serviceTime || 0,
    });
    onBack();
  };

  const handleSelectProduct = (product) => {
    actions.addLineItem({
      type: 'product',
      id: `product_added_${Date.now()}`,
      // The real catalogue product id — backend keys stock + commission off this.
      // Fall back across id field names so it's never the synthetic `id` above.
      serviceId: product._id || product.id || product.productId,
      name: product.productName || product.name || '',
      originalPrice: parseFloat(product.retailPrice || product.price) || 0,
      // Carry the catalogue stock price + commission flag so the backend can
      // compute the stylist's product commission (needs commissionEnable===1).
      stockPrice: parseFloat(product.stockPrice) || 0,
      commissionEnable: product.commissionEnable || 0,
      // Carried so the line can be resolved in the checkout sidebar, exactly
      // as it is in the POS cart — same picker, same behaviour.
      options: product.options || [],
      variants: product.variants || [],
      variantId: null,
      variantLabel: '',
      trackStock: product.trackStock,
      stockQty: product.quantity || 0,
      staffId: state.booking?.hairdresserId || state.lineItems[0]?.staffId || localStorage.getItem('userId'),
      staffName: state.lineItems[0]?.staffName || state.booking?.hairdresser?.name || '',
      discount: null,
      diaryColour: null,
    });
    onBack();
  };

  // Filter services by search
  const filteredCategories = serviceCategories.map(cat => ({
    ...cat,
    services: (cat.services || []).filter(svc =>
      !searchQuery || (svc.serviceName || '').toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter(cat => cat.services.length > 0);

  const filteredProducts = products.filter(p =>
    !searchQuery || (p.productName || p.name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredVouchers = voucherCatalogs.filter(v =>
    !searchQuery || (v.name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Type selection
  if (step === 'type') {
    return (
      <div>
        <div className="co-screen-header">
          <button className="co-back-btn" onClick={onBack}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <h2 className="co-screen-title">Add Item</h2>
        </div>

        <div className="co-add-type-list">
          <button className="co-add-type-btn" onClick={() => { setStep('services'); setSearchQuery(''); }}>
            <div className="co-add-type-icon" style={{ backgroundColor: '#29282D' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
              </svg>
            </div>
            <div>
              <div className="co-add-type-label">Service</div>
              <div className="co-add-type-sub">Add a salon service</div>
            </div>
          </button>

          <button className="co-add-type-btn" onClick={() => { setStep('products'); setSearchQuery(''); }}>
            <div className="co-add-type-icon" style={{ backgroundColor: '#6366f1' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <path d="M16 10a4 4 0 01-8 0" />
              </svg>
            </div>
            <div>
              <div className="co-add-type-label">Product</div>
              <div className="co-add-type-sub">Sell a retail product</div>
            </div>
          </button>

          <button className="co-add-type-btn" onClick={() => { setStep('vouchers'); setSearchQuery(''); }}>
            <div className="co-add-type-icon" style={{ backgroundColor: '#f59e0b' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4" />
                <path d="M4 6v12c0 1.1.9 2 2 2h14v-4" />
                <path d="M18 12a2 2 0 0 0 0 4h4v-4h-4z" />
              </svg>
            </div>
            <div>
              <div className="co-add-type-label">Voucher</div>
              <div className="co-add-type-sub">Sell a gift voucher</div>
            </div>
          </button>
        </div>
      </div>
    );
  }

  // Service, Product, or Voucher picker
  const isServices = step === 'services';
  const isVouchers = step === 'vouchers';
  const title = isServices ? 'Add Service' : isVouchers ? 'Sell Voucher' : 'Add Product';

  // Inline amount step — chosen denomination / custom amount + promo, rendered
  // as a proper step within the panel (no overlay) on desktop and mobile.
  if (step === 'voucherAmount' && pickerVoucher) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div className="co-screen-header">
          <button className="co-back-btn" onClick={() => { setPickerVoucher(null); setStep('vouchers'); }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <h2 className="co-screen-title">{pickerVoucher.name || 'Gift Voucher'}</h2>
        </div>
        <div style={{ padding: '20px 22px', overflowY: 'auto', flex: 1 }}>
          <VoucherAmountFields
            voucher={pickerVoucher}
            currency={currencySymbol}
            onConfirm={handleVoucherAmountConfirmed}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="co-screen-header">
        <button className="co-back-btn" onClick={() => setStep('type')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h2 className="co-screen-title">{title}</h2>
      </div>

      <div className="co-picker-search">
        <span className="co-picker-search-icon"><SearchIcon /></span>
        <input
          className="co-picker-search-input"
          placeholder={isServices ? 'Search services...' : isVouchers ? 'Search vouchers...' : 'Search products...'}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          autoFocus
        />
      </div>

      {loading ? (
        <div className="co-loading"><div className="co-spinner" /></div>
      ) : (
        <div className="co-picker-list">
          {isServices ? (
            filteredCategories.length > 0 ? (
              filteredCategories.map((cat, ci) => (
                <div className="co-picker-category" key={ci}>
                  <div className="co-picker-category-label">{cat.categoryName || cat.name || 'Services'}</div>
                  {cat.services.map((svc, si) => (
                    <button
                      className="co-picker-item"
                      key={si}
                      onClick={() => handleSelectService(svc, cat.categoryName)}
                    >
                      <span className="co-picker-item-name">{svc.serviceName}</span>
                      <span className="co-picker-item-price">{fmt(svc.servicePrice)}</span>
                    </button>
                  ))}
                </div>
              ))
            ) : (
              <p className="co-picker-empty">No services found</p>
            )
          ) : isVouchers ? (
            filteredVouchers.length > 0 ? (
              filteredVouchers.map((voucher, vi) => (
                <button
                  className="co-picker-item"
                  key={vi}
                  onClick={() => handleSelectVoucher(voucher)}
                >
                  <span className="co-picker-item-name">{voucher.name}</span>
                  <span className="co-picker-item-price">{fmt(voucher.value)}</span>
                </button>
              ))
            ) : (
              <p className="co-picker-empty">No vouchers found. Create voucher templates in the Vouchers section first.</p>
            )
          ) : (
            filteredProducts.length > 0 ? (
              filteredProducts.map((product, pi) => (
                <button
                  className="co-picker-item"
                  key={pi}
                  onClick={() => handleSelectProduct(product)}
                >
                  <span className="co-picker-item-name">{product.productName || product.name}</span>
                  <span className="co-picker-item-price">{fmt(product.retailPrice || product.price)}</span>
                </button>
              ))
            ) : (
              <p className="co-picker-empty">No products found</p>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default AddItemScreen;
